import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Inject, Injectable, Optional } from '@angular/core';
import { defer, lastValueFrom, map, Observable, of, shareReplay } from 'rxjs';

import { Environment, ENVIRONMENT } from '@environment';
import { LocationService } from '@usheru-hff/sdk/location';
import { UsheruApi } from '@usheru-hff/shared/data-access-backend';
import {
  Channel,
  ChannelSettings,
  Company,
  Country,
  CustomPage,
  Language,
  posGetMovieFromApi,
  Tag,
  UserRole,
  Video
} from '@usheru-hff/shared/domain';
import { GoogleMapsLoaderService } from '@usheru-hff/shared/feature-location-selector';
import { TemplatesService } from '@usheru-hff/shared/utils';
import { TranslationsService } from '@usheru-hff/shared/utils-translation';

@Injectable({
  providedIn: 'root'
})
export class ChannelService {
  /**
   * TOFIX
   * This service shouldn't be here. There is an issue and this service don't
   * work as singelton.
   */
  public readonly templatesService = inject(TemplatesService);
  private readonly DEFAULT_LANG: Language = {
    id: 1,
    name: 'English',
    englishName: 'English',
    code: 'en',
    code3: 'eng',
    slug: 'english',
    trMsg: 'language.name.english',
    default: true
  };

  private _channel: Channel;
  private $channelTags: Tag[];
  private rightOwners: Company[];
  private readonly headers = this.usheruApi.getStandardHeader();

  constructor(
    private usheruApi: UsheruApi,
    private http: HttpClient,
    private locationService: LocationService,
    @Inject(ENVIRONMENT) private env: Environment,
    @Inject(DOCUMENT) private document: Document,
    @Optional() private translationService: TranslationsService,
    private googleMapsLoader: GoogleMapsLoaderService
  ) {}

  public init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const channelPromise = lastValueFrom(this.getChannel());
      return channelPromise.then(channel => {
        if (!channel) {
          console.error('ChannelService failed to load data:');
          reject();
        }
        this._channel = channel;

        this.translationService.channel = channel;
        this.translationService.channelLanguages = this.mapChannelLanguagesOrDefault(channel);
        if (!this.translationService.language) this.translationService.language = channel.languages[0];
        this.locationService.loadInitialLocation(channel);
        this.googleMapsLoader.disabledMaps = !!channel?.settings?.disableShowtimes;
        resolve();
      });
    });
  }

  private channel$ = defer(() =>
    this.http.get<Channel>(`${this.env.api?.url}/channels`, { headers: this.headers }).pipe(
      map(response => {
        const { settings, customPages, ...channelProperties } = response;
        // HACK: `settings` comes as string and we transform it to ChannelSettings here
        const channelSettings = settings ? <ChannelSettings>JSON.parse(settings as string) : {};
        const channelCustomPages = this.toCustomPages(customPages);
        const channel = new Channel();
        Object.assign(channel, { ...channelProperties, settings: channelSettings, customPages: channelCustomPages });
        return channel;
      })
    )
  ).pipe(shareReplay(1));

  private getChannel(): Observable<Channel> {
    return this.channel$;
  }

  public getRightOwnersList(): Observable<Company[]> {
    if (this.rightOwners) {
      return of(this.rightOwners);
    } else {
      return this.getRightOwnersApi();
    }
  }

  getChannelAvailableLanguages(): Observable<Language[]> {
    return this.channel$.pipe(map(this.mapChannelLanguagesOrDefault));
  }

  /**
   * Note: Hardcoded solution for DNK
   *
   * @param countryCode3 - 'code3' matches language code3
   *
   * @returns
   */
  getChannelLanguageFromCountryCode(countryCode3: string): Language {
    const tempCountryCode: string = countryCode3 === 'DNK' ? 'dan' : '';
    return (
      this.channel.languages?.find(lang =>
        [countryCode3.toLocaleLowerCase(), tempCountryCode].includes(lang.code3!.toLowerCase())
      ) || this.DEFAULT_LANG
    );
  }

  public getChannelAvailableCountries(): Observable<Country[]> {
    return this.channel$.pipe(map(this.mapChannelCountriesOrEmpty));
  }

  private mapChannelLanguagesOrDefault(channel: Channel): Language[] {
    return channel.languages || [this.DEFAULT_LANG];
  }

  private mapChannelCountriesOrEmpty(channel: Channel): Country[] {
    return channel.partner?.locationRestrictions || [];
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toCustomPages(items: any[]): CustomPage[] {
    if (items) {
      return items.map(item => {
        const { structure, ...customPage } = item;
        if (structure) {
          customPage.structure = JSON.parse(structure);
        }
        return <CustomPage>customPage;
      });
    }
    return [];
  }

  public get channel(): Channel {
    return this._channel;
  }

  public isChannelVideoEmbed(): boolean {
    return this.channel.settings.sliderEmbeddedVideo || this.env?.customPageParams?.channelVideoEmbed;
  }

  public isChannelVideoAutoplay(): boolean {
    return this.channel.settings.sliderAutoplayVideo || this.env?.customPageParams?.channelVideoAutoplay;
  }

  isRolePresent(role: UserRole): boolean {
    return this.channel?.userRoles?.includes(role);
  }

  getCommunityRoleAvailable(): UserRole {
    return (
      this.channel?.userRoles?.find(r => r === UserRole.social) ||
      this.channel?.userRoles?.find(r => r === UserRole.community)
    );
  }

  public getChannelTags(): Observable<Tag[]> {
    if (this.$channelTags) {
      return of(this.$channelTags);
    } else {
      return this.channelTagsApi();
    }
  }

  private channelTagsApi(): Observable<Tag[]> {
    return this.http.get<Tag[]>(this.env.api.channelTags(), {
      headers: this.usheruApi.getStandardHeader()
    });
  }

  private getRightOwnersApi(): Observable<Company[]> {
    //TODO: add api conection for rightOwners
    return this.http.get<Company[]>(this.env.api.channelContractualPartners(), {
      headers: this.usheruApi.getStandardHeader()
    });
  }

  getMoviesOffset(): number {
    return this.channel?.settings?.moviesPageSize || 10;
  }

  getDefaultImg(orientation: 'landscape' | 'portrait'): string {
    return orientation === 'landscape' ? this.channel.tilePhotoUrl : this.channel.portraitPhotoUrl;
  }

  isCommunityEnabled(): boolean {
    return this.channel?.userRoles?.includes(UserRole.community);
  }
}
